import React, { useDeferredValue, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaCloudDownloadAlt, FaPlus } from "react-icons/fa";
import adminApi from "services/admin-api";
import { useAuth } from "context/auth-context";
import { DataTableSkeleton } from "components/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { DataTable } from "components/molecules/table/table";
import RightDrawer from "components/global/RightDrawer";
import { PageHeader, StatusPill } from "components/admin/ui";
import { TeamForm } from "components/molecules/forms/team-form/team-form";
import { getTeamsWorkspaceContent } from "pages/app/teams/teams-access";
import { isRecoverableUploadResponseError } from "utils/admin";
import { hasPortalAction } from "utils/portal-access";
import {
  TeamsPrimaryButton,
  TeamsInfoRow,
  TeamsGridLayout,
  TeamsThumbnailImage,
  TeamsFieldInput,
  TeamsLabelText,
  TeamsMetaText,
  TeamsFieldSelect,
  TeamsSecondaryButton,
  TeamsDrawerActions,
  TeamsDrawerForm,
  TeamsDrawerStack,
  TeamsBodyText,
  TeamsResultList,
  TeamsResultButton,
  TeamsResultText,
  TeamsResultMeta,
} from "pages/app/teams/styles/teams.inline.styles";

const apiSportsOptions = [
  { label: "Football", value: "football" },
  { label: "Basketball", value: "basketball" },
  { label: "Rugby", value: "rugby" },
  { label: "Hockey", value: "hockey" },
  { label: "Baseball", value: "baseball" },
  { label: "Handball", value: "handball" },
];

const sportSeasonPlaceholders: Record<string, string> = {
  football: "2024",
  basketball: "2024-2025",
  rugby: "2024",
  hockey: "2024",
  baseball: "2024",
  handball: "2024",
};

type ApiSportsSearchResult = {
  country?: string;
  external_id: number;
  logo_url?: string;
  name: string;
  source?: string;
  sport: string;
};

const TeamsPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { portalAccess } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [sportFilter, setSportFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTeam, setSelectedTeam] = useState<any | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isImportDrawerOpen, setImportDrawerOpen] = useState(false);
  const [importSport, setImportSport] = useState("football");
  const [importSearchValue, setImportSearchValue] = useState("");
  const [importSeason, setImportSeason] = useState("2024");
  const [selectedImportResult, setSelectedImportResult] = useState<ApiSportsSearchResult | null>(
    null,
  );
  const deferredSearch = useDeferredValue(searchValue);
  const deferredImportSearch = useDeferredValue(importSearchValue);
  const { data: teams = [], isLoading } = useQuery({
    queryKey: ["teams"],
    queryFn: () => adminApi.teams.list(),
  });
  const { data: sports = [] } = useQuery({
    queryKey: ["sports"],
    queryFn: () => adminApi.sports.list(),
  });
  const { data: countries = [] } = useQuery({
    queryKey: ["countries"],
    queryFn: () => adminApi.countries.list(),
  });
  const { data: apiSportsSearchResults = [], isFetching: isSearchingApiSports } = useQuery({
    queryKey: ["teams", "api-sports-search", importSport, deferredImportSearch],
    queryFn: () =>
      adminApi.teams.searchApiSports({
        q: deferredImportSearch.trim(),
        sport: importSport,
      }),
    enabled: isImportDrawerOpen && deferredImportSearch.trim().length >= 2,
  });
  const canManageTeams = hasPortalAction(portalAccess, "teams.manage");
  const workspaceContent = getTeamsWorkspaceContent(portalAccess, canManageTeams);
  const selectedApiSportOption = apiSportsOptions.find((option) => option.value === importSport);
  const importHelpText =
    importSport === "football"
      ? "Football imports can bring in full rosters and season stats. Use season 2024 on the current free API-Sports plan."
      : importSport === "basketball"
        ? "Basketball imports bring in team athletes. Use a season like 2024-2025."
        : importSport === "rugby"
          ? "Rugby imports currently bring in the team profile and branding only. API-Sports does not expose rugby roster data from this product yet, so athlete sync stays at zero."
          : `${selectedApiSportOption?.label || "This sport"} imports currently create the team profile only unless API-Sports exposes athlete rosters for that product.`;
  const saveMutation = useMutation({
    mutationFn: (payload: any) =>
      selectedTeam
        ? adminApi.teams.update(selectedTeam.id, payload)
        : adminApi.teams.create(payload),
    onSuccess: () => {
      toast.success(selectedTeam ? "Team updated." : "Team created.");
      queryClient.invalidateQueries({
        queryKey: ["teams"],
      });
      setDrawerOpen(false);
      setSelectedTeam(null);
    },
    onError: (error: any, variables: any) => {
      if (isRecoverableUploadResponseError(error, variables)) {
        toast.success("Team saved and image refreshed.");
        queryClient.invalidateQueries({
          queryKey: ["teams"],
        });
        setDrawerOpen(false);
        setSelectedTeam(null);
        return;
      }
      toast.error(error?.response?.data?.error || "Unable to save this team.");
    },
  });
  const importMutation = useMutation({
    mutationFn: () =>
      adminApi.teams.importApiSports({
        sport: importSport,
        team_id: selectedImportResult?.external_id,
        season: importSeason.trim() || undefined,
      }),
    onSuccess: (result: any) => {
      const importedSport = result?.sport || importSport;
      const importedPlayersCount = result?.imported_players_count ?? 0;
      const importedTeamName = result?.team?.name || "Team";
      const successMessage =
        importedPlayersCount > 0
          ? `${importedTeamName} imported from ${importedSport}. ${importedPlayersCount} athlete${importedPlayersCount === 1 ? "" : "s"} synced.`
          : `${importedTeamName} imported from ${importedSport}. Team profile is ready, and athlete sync is not available for this provider feed yet.`;
      toast.success(successMessage);
      queryClient.invalidateQueries({
        queryKey: ["teams"],
      });
      queryClient.invalidateQueries({
        queryKey: ["athletes"],
      });
      queryClient.invalidateQueries({
        queryKey: ["sports"],
      });
      setImportDrawerOpen(false);
      setSelectedImportResult(null);
      setImportSearchValue("");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to import this API-Sports team.");
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.teams.remove(id),
    onSuccess: () => {
      toast.success("Team deleted.");
      queryClient.invalidateQueries({
        queryKey: ["teams"],
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to delete this team.");
    },
  });
  const filteredTeams = teams.filter((team: any) => {
    const matchesSearch = [team.name, team.sport, team.country, team.level]
      .join(" ")
      .toLowerCase()
      .includes(deferredSearch.trim().toLowerCase());
    const matchesSport = sportFilter === "all" || team.sport === sportFilter;
    const matchesCountry = countryFilter === "all" || team.country === countryFilter;
    const matchesStatus = statusFilter === "all" || team.status === statusFilter;
    return matchesSearch && matchesSport && matchesCountry && matchesStatus;
  });
  return (
    <AppLayout route="/teams">
      <PageHeader
        title="Teams"
        description={workspaceContent.pageDescription}
        actions={
          canManageTeams ? (
            <div className="flex flex-wrap items-center gap-3">
              <TeamsSecondaryButton
                onClick={() => {
                  setImportDrawerOpen(true);
                }}
                type="button"
              >
                <FaCloudDownloadAlt size={12} />
                Import from API-Sports
              </TeamsSecondaryButton>
              <TeamsPrimaryButton
                onClick={() => {
                  setSelectedTeam(null);
                  setDrawerOpen(true);
                }}
                type="button"
              >
                <FaPlus size={12} />
                Add team
              </TeamsPrimaryButton>
            </div>
          ) : null
        }
      />

      <DataTable
        columns={[
          {
            label: "Team",
            accessor: (row: any) => (
              <TeamsInfoRow>
                <TeamsThumbnailImage
                  alt={row.name || "Team"}
                  fallbackLabel={row.name}
                  src={row.image_url}
                  variant="team"
                />
                <div>
                  <TeamsLabelText>{row.name}</TeamsLabelText>
                  <TeamsMetaText>{row.sport || "Sport not linked yet"}</TeamsMetaText>
                </div>
              </TeamsInfoRow>
            ),
          },
          {
            label: "Country",
            accessor: (row: any) => row.country || "Unspecified",
          },
          {
            label: "Level",
            accessor: (row: any) => row.level || "Unspecified",
          },
          {
            label: "Players",
            accessor: (row: any) => row.player_count ?? "N/A",
          },
          {
            label: "Status",
            accessor: (row: any) => <StatusPill value={row.status} />,
          },
        ]}
        data={filteredTeams}
        emptyDescription={workspaceContent.emptyDescription}
        emptyTitle={workspaceContent.emptyTitle}
        isLoading={isLoading}
        loadingState={<DataTableSkeleton columns={5} rows={5} showThumbnail />}
        onEdit={
          canManageTeams
            ? (team) => {
                setSelectedTeam(team);
                setDrawerOpen(true);
              }
            : undefined
        }
        onRemove={
          canManageTeams
            ? (id) => {
                if (window.confirm("Delete this team? This action cannot be undone.")) {
                  deleteMutation.mutate(String(id));
                }
              }
            : undefined
        }
        onView={(id) => history.push(`/teams/${id}`)}
        toolbar={
          <TeamsGridLayout>
            <TeamsFieldInput
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search teams by name, sport, country, or level"
              value={searchValue}
            />
            <TeamsFieldSelect
              onChange={(event) => setSportFilter(event.target.value)}
              value={sportFilter}
            >
              <option value="all">All sports</option>
              {sports.map((sport: any) => (
                <option key={sport.id} value={sport.name}>
                  {sport.name}
                </option>
              ))}
            </TeamsFieldSelect>
            <TeamsFieldSelect
              onChange={(event) => setCountryFilter(event.target.value)}
              value={countryFilter}
            >
              <option value="all">All countries</option>
              {countries.map((country: any) => (
                <option key={country.name} value={country.name}>
                  {country.name}
                </option>
              ))}
            </TeamsFieldSelect>
            <TeamsFieldSelect
              onChange={(event) => setStatusFilter(event.target.value)}
              value={statusFilter}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </TeamsFieldSelect>
          </TeamsGridLayout>
        }
      />

      <RightDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedTeam(null);
        }}
        title={selectedTeam ? "Edit team" : "Create team"}
      >
        <TeamForm
          countries={countries.map((country: any) => ({
            label: country.name,
            value: country.name,
          }))}
          initialValues={{
            name: selectedTeam?.name || "",
            sport: selectedTeam?.sport || "",
            country: selectedTeam?.country || "",
            level: selectedTeam?.level || "",
            image: selectedTeam?.image_url || "",
            status: selectedTeam?.status || "active",
          }}
          onCancel={() => {
            setDrawerOpen(false);
            setSelectedTeam(null);
          }}
          onSubmit={(values) => saveMutation.mutate(values)}
          sports={sports.map((sport: any) => ({
            label: sport.name,
            value: sport.name,
          }))}
        />
      </RightDrawer>

      <RightDrawer
        isOpen={isImportDrawerOpen}
        onClose={() => {
          if (!importMutation.isPending) {
            setImportDrawerOpen(false);
          }
        }}
        title="Import from API-Sports"
      >
        <TeamsDrawerForm
          onSubmit={(event) => {
            event.preventDefault();

            if (!selectedImportResult) {
              toast.error("Select a team from the API-Sports results first.");
              return;
            }

            if (importSport === "basketball" && !importSeason.trim()) {
              toast.error("Basketball imports require a season.");
              return;
            }

            importMutation.mutate();
          }}
        >
          <TeamsDrawerStack>
            <TeamsBodyText>{importHelpText}</TeamsBodyText>
          </TeamsDrawerStack>

          <TeamsDrawerStack>
            <TeamsLabelText>Sport</TeamsLabelText>
            <TeamsFieldSelect
              onChange={(event) => {
                const nextSport = event.target.value;
                setImportSport(nextSport);
                setSelectedImportResult(null);
                if (!importSeason.trim()) {
                  setImportSeason(sportSeasonPlaceholders[nextSport] || "");
                }
              }}
              value={importSport}
            >
              {apiSportsOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TeamsFieldSelect>
          </TeamsDrawerStack>

          <TeamsDrawerStack>
            <TeamsLabelText>Search team</TeamsLabelText>
            <TeamsFieldInput
              onChange={(event) => {
                setImportSearchValue(event.target.value);
                setSelectedImportResult(null);
              }}
              placeholder={`Search ${selectedApiSportOption?.label?.toLowerCase() || "sport"} teams`}
              value={importSearchValue}
            />
            {deferredImportSearch.trim().length >= 2 ? (
              <TeamsResultList>
                {isSearchingApiSports ? (
                  <TeamsMetaText>Searching API-Sports teams...</TeamsMetaText>
                ) : apiSportsSearchResults.length ? (
                  apiSportsSearchResults.map((result: ApiSportsSearchResult) => (
                    <TeamsResultButton
                      key={`${result.sport}-${result.external_id}`}
                      onClick={() => setSelectedImportResult(result)}
                      type="button"
                    >
                      <TeamsResultText>
                        <TeamsLabelText>{result.name}</TeamsLabelText>
                        <TeamsResultMeta>
                          {[result.sport, result.country, `External ID ${result.external_id}`]
                            .filter(Boolean)
                            .join(" | ")}
                        </TeamsResultMeta>
                      </TeamsResultText>
                      {selectedImportResult?.external_id === result.external_id &&
                      selectedImportResult?.sport === result.sport ? (
                        <TeamsMetaText>Selected</TeamsMetaText>
                      ) : null}
                    </TeamsResultButton>
                  ))
                ) : (
                  <TeamsMetaText>No API-Sports teams matched that search.</TeamsMetaText>
                )}
              </TeamsResultList>
            ) : (
              <TeamsMetaText>Type at least 2 characters to search.</TeamsMetaText>
            )}
          </TeamsDrawerStack>

          <TeamsDrawerStack>
            <TeamsLabelText>Season</TeamsLabelText>
            <TeamsFieldInput
              onChange={(event) => setImportSeason(event.target.value)}
              placeholder={sportSeasonPlaceholders[importSport] || "2024"}
              value={importSeason}
            />
            <TeamsMetaText>
              Use `2024` for football, `2024-2025` for basketball, and leave it as reference data
              for rugby team imports.
            </TeamsMetaText>
          </TeamsDrawerStack>

          {selectedImportResult ? (
            <TeamsDrawerStack>
              <TeamsLabelText>Selected team</TeamsLabelText>
              <TeamsResultList>
                <TeamsResultText>
                  <TeamsLabelText>{selectedImportResult.name}</TeamsLabelText>
                  <TeamsResultMeta>
                    {[
                      selectedImportResult.sport,
                      selectedImportResult.country,
                      `External ID ${selectedImportResult.external_id}`,
                    ]
                      .filter(Boolean)
                      .join(" | ")}
                  </TeamsResultMeta>
                </TeamsResultText>
              </TeamsResultList>
            </TeamsDrawerStack>
          ) : null}

          <TeamsDrawerActions>
            <TeamsSecondaryButton
              onClick={() => {
                setImportDrawerOpen(false);
                setSelectedImportResult(null);
              }}
              type="button"
            >
              Cancel
            </TeamsSecondaryButton>
            <TeamsPrimaryButton disabled={importMutation.isPending} type="submit">
              {importMutation.isPending ? "Importing..." : "Import team"}
            </TeamsPrimaryButton>
          </TeamsDrawerActions>
        </TeamsDrawerForm>
      </RightDrawer>
    </AppLayout>
  );
};
export default TeamsPage;
